/**
 * Model Defaults Configuration Panel
 * Configure system-wide default models for all categories
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Loading } from "@/components/common";
import {
  Save,
  RotateCcw,
  AlertCircle,
  MessageSquare,
  Eye,
  Brain,
  ArrowUpDown,
  Mic,
  Lock,
  Image as ImageIcon,
  Sparkles,
  Play,
  Square,
  PhoneCall,
} from "lucide-react";
import { audioApi } from "@/lib/api/audio";
import { API_BASE_URL } from "@/lib/api/client";
import type {
  AudioSettings,
  AudioProviderMeta,
  VoiceCallProviderMeta,
  TTSProductProfile,
  VoiceCallProductProfile,
} from "@/types/system";
import {
  useModelDefaultsConfig,
  useUpdateModelDefaultsConfig,
} from "../hooks/useSystemConfig";
import { ModelSelect } from "@/components/model-display";
import { ModelParameters } from "@/components/model-display/ModelParameters";
import { useI18n } from "@/i18n";
import type { ModelDefaultsConfig } from "@/types/system";
import type { Modality } from "@/types";
import { useCatalogModels } from "@/lib/queries/useCatalogModels";
import { modelId as getModelId } from "@/lib/models/catalogAccessors";

interface ModelFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  modality: Modality;
  /** Renders a 直连/Direct badge above the selector. Use for slots that
   *  bypass LiteLLM and call the vendor API directly (e.g. TTS). */
  directConnect?: boolean;
  /** Further narrow text-modality results to capability-compatible models only. */
  capabilityFilter?: 'vision' | 'reasoning' | 'function_calling';
}

const ModelField: React.FC<ModelFieldProps> = ({
  label,
  value,
  onChange,
  description,
  modality,
  directConnect = false,
  capabilityFilter,
}) => (
  <div className="space-y-1.5">
    {directConnect && (
      <div className="flex items-center gap-2 -mb-0.5">
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-warning/10 text-warning border border-warning/20">
          直连 · Direct
        </span>
        <span className="text-[11px] text-warning">
          绕过 LiteLLM 直接调用厂商 API（LiteLLM 暂不支持 TTS），模型 ID 仅作展示，运行时由服务端硬编码
        </span>
      </div>
    )}
    {label && <p className="text-sm font-medium text-foreground">{label}</p>}
    <ModelSelect
      modality={modality}
      value={value}
      onChange={onChange}
      variant="compact"
      capabilityFilter={capabilityFilter}
    />
    {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
  </div>
);

type ModelDefaultsCategory = keyof Omit<
  ModelDefaultsConfig,
  "version" | "updated_by" | "updated_at" | "_meta"
>;

interface ModelParamsProps {
  category: ModelDefaultsCategory;
  formData: ModelDefaultsConfig;
  updateField: (
    category: ModelDefaultsCategory,
    field: string,
    value: any,
  ) => void;
  hasTemperature?: boolean;
  hasTopP?: boolean;
  hasMaxTokens?: boolean;
  hasTopK?: boolean;
}

const ModelParams: React.FC<ModelParamsProps> = ({
  category,
  formData,
  updateField,
  hasTemperature = false,
  hasTopP = false,
  hasMaxTokens = false,
  hasTopK = false,
}) => {
  const data = (formData as any)[category] || {};
  const { data: textModels } = useCatalogModels('text');

  const hasSamplingParams = hasTemperature || hasTopP || hasMaxTokens;

  // Resolve the selected model against the text catalog for sampling params
  const catalogModel = useMemo(() => {
    if (!hasSamplingParams || !data.model_id) return undefined;
    return textModels?.find((m) => getModelId(m) === data.model_id);
  }, [hasSamplingParams, data.model_id, textModels]);

  // Build param values keyed by param name for ModelParameters
  const paramValues = useMemo<Record<string, number | boolean | string>>(() => {
    const vals: Record<string, number | boolean | string> = {};
    if (hasTemperature && data.temperature !== undefined && data.temperature !== null) {
      vals.temperature = data.temperature;
    }
    if (hasTopP && data.top_p !== undefined && data.top_p !== null) {
      vals.top_p = data.top_p;
    }
    if (hasMaxTokens && data.max_tokens !== undefined && data.max_tokens !== null) {
      vals.max_tokens = data.max_tokens;
    }
    return vals;
  }, [hasTemperature, hasTopP, hasMaxTokens, data.temperature, data.top_p, data.max_tokens]);

  const handleParamChange = useCallback(
    (paramName: string, paramValue: number | boolean | string) => {
      updateField(category, paramName, paramValue);
    },
    [category, updateField],
  );

  if ((!hasSamplingParams || !catalogModel) && !hasTopK) return null;

  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      {catalogModel && hasSamplingParams && (
        <ModelParameters
          model={catalogModel}
          values={paramValues}
          onChange={handleParamChange}
        />
      )}
      {hasTopK && (
        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs text-gray-500 w-12">Top K</label>
          <input
            type="number"
            step="1"
            min="1"
            max="100"
            className="w-20 px-2 py-1 text-sm border rounded"
            value={data.top_k ?? ""}
            onChange={(e) =>
              updateField(
                category,
                "top_k",
                e.target.value ? parseInt(e.target.value) : undefined,
              )
            }
          />
        </div>
      )}
    </div>
  );
};

interface ModelCategoryCardProps {
  title: string;
  icon: React.ReactNode;
  badge?: string;
  children: React.ReactNode;
}

const ModelCategoryCard: React.FC<ModelCategoryCardProps> = ({
  title,
  icon,
  badge,
  children,
}) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
      {icon}
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {badge && (
        <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {badge}
        </span>
      )}
    </div>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </Card>
);

export interface SpeechModelsSectionHandle {
  saveAudio: () => Promise<void>;
  hasAudioChanges: () => boolean;
}

interface SpeechModelsSectionProps {
  formData: ModelDefaultsConfig;
  updateField: (category: ModelDefaultsCategory, field: string, value: any) => void;
  t: any;
  onAudioDirtyChange?: (dirty: boolean) => void;
}

const SpeechModelsSection = React.forwardRef<SpeechModelsSectionHandle, SpeechModelsSectionProps>(({
  formData,
  updateField,
  t,
  onAudioDirtyChange,
}, ref) => {
  const at = t.audioSettings;
  const [audioConfig, setAudioConfig] = useState<AudioSettings | null>(null);
  const [providerMeta, setProviderMeta] = useState<AudioProviderMeta | null>(null);
  const [voiceCallMeta, setVoiceCallMeta] = useState<VoiceCallProviderMeta | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(true);
  const initialAudioConfigRef = useRef<string>("");
  const [isPreviewing, setIsPreviewing] = useState(false);
  const previewAbortRef = useRef<AbortController | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);

  const handlePreview = async (product?: 'dora' | 'soulmate') => {
    if (isPreviewing) {
      // Stop preview
      previewAbortRef.current?.abort();
      sourceNodeRef.current?.stop();
      setIsPreviewing(false);
      return;
    }

    const profile = product ? audioConfig?.tts?.profiles?.[product] : undefined;
    const provider = profile?.provider || audioConfig?.tts?.provider || "zhipu";
    const providerConfig = (audioConfig?.tts?.providers?.[provider] as Record<string, unknown>) || {};
    const voice =
      (profile?.voice as string | undefined) ||
      (providerConfig.voice as string | undefined) ||
      (providerConfig.default_voice as string | undefined) ||
      providerMeta?.voices?.[0]?.id ||
      "tongtong";
    const speed = profile?.speed ?? audioConfig?.tts?.default_speed ?? 1.0;
    const voiceStrategy = product === "dora" ? "fixed" : profile?.voice_strategy;
    const speechProfile = voiceStrategy === "profile_match"
      ? {
          identity: { gender: product === "soulmate" ? "male" : "female" },
          persona_summary: product === "soulmate" ? "成熟、沉稳、温柔、低声陪伴" : "温柔、清晰、稳定",
        }
      : undefined;

    setIsPreviewing(true);
    const abort = new AbortController();
    previewAbortRef.current = abort;

    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/audio/tts/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}`,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            input: "你好，这是一段语音预览测试。欢迎使用智能语音合成服务。",
            product,
            provider,
            voice: voiceStrategy === "profile_match" ? undefined : voice,
            speed,
            speech_profile: speechProfile,
          }),
          signal: abort.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`TTS preview request failed (${response.status})`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader");

      const pcmChunks: Uint8Array[] = [];
      const decoder = new TextDecoder();
      let buffer = "";
      let sampleRate = 24000;

      let reading = true;
      while (reading) {
        const { done, value } = await reader.read();
        if (done) {
          reading = false;
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const evt = JSON.parse(data);
            if (evt.error) {
              throw new Error(evt.error.message || "TTS preview failed");
            }
            if (typeof evt.sample_rate === "number" && evt.sample_rate > 0) {
              sampleRate = evt.sample_rate;
            }
            if (evt.audio) {
              const binary = atob(evt.audio);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
              if (bytes.length > 0) pcmChunks.push(bytes);
            }
          } catch (err) {
            if (err instanceof Error) throw err;
            throw new Error("Invalid TTS preview stream event");
          }
        }
      }

      if (abort.signal.aborted) return;

      // Combine PCM chunks and play via Web Audio API
      const totalLen = pcmChunks.reduce((s, c) => s + c.length, 0);
      if (totalLen <= 0) {
        throw new Error("TTS preview returned no audio. Check provider credentials and voice configuration.");
      }
      const pcm = new Uint8Array(totalLen);
      let offset = 0;
      for (const chunk of pcmChunks) {
        pcm.set(chunk, offset);
        offset += chunk.length;
      }

      // Convert 16-bit PCM to Float32 for Web Audio
      if (pcm.length % 2 !== 0) {
        throw new Error("TTS preview returned invalid 16-bit PCM data.");
      }
      const samples = pcm.length / 2;
      const float32 = new Float32Array(samples);
      const view = new DataView(pcm.buffer);
      for (let i = 0; i < samples; i++) {
        float32[i] = view.getInt16(i * 2, true) / 32768;
      }

      const ctx = audioCtxRef.current || new AudioContext({ sampleRate });
      audioCtxRef.current = ctx;
      const audioBuffer = ctx.createBuffer(1, float32.length, sampleRate);
      audioBuffer.getChannelData(0).set(float32);
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      sourceNodeRef.current = source;
      source.onended = () => setIsPreviewing(false);
      source.start();
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        console.error("TTS preview error:", err);
        alert(`Preview failed: ${err?.message || err}`);
      }
      setIsPreviewing(false);
    }
  };

  const fetchProviderMeta = useCallback(async (providerId: string) => {
    try {
      const res = await audioApi.getProviderMeta(providerId);
      setProviderMeta(res.data);
    } catch {
      setProviderMeta(null);
    }
  }, []);

  const fetchVoiceCallMeta = useCallback(async (providerId: string) => {
    try {
      const res = await audioApi.getVoiceCallProviderMeta(providerId);
      setVoiceCallMeta(res.data);
    } catch {
      setVoiceCallMeta(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await audioApi.getConfig();
        if (!cancelled) {
          setAudioConfig(res.data);
          initialAudioConfigRef.current = JSON.stringify(res.data);
          if (res.data?.tts?.provider) {
            fetchProviderMeta(res.data.tts.provider);
          }
          if (res.data?.voice_call?.provider) {
            fetchVoiceCallMeta(res.data.voice_call.provider);
          }
        }
      } catch {
        // API may not be available yet
      } finally {
        if (!cancelled) setLoadingAudio(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchProviderMeta, fetchVoiceCallMeta]);

  const updateAudioField = (
    section: "tts" | "stt" | "voice_call",
    field: string,
    value: unknown,
  ) => {
    if (!audioConfig) return;
    const updated = {
      ...audioConfig,
      [section]: {
        ...audioConfig[section],
        [field]: value,
      },
    };
    setAudioConfig(updated);
    onAudioDirtyChange?.(JSON.stringify(updated) !== initialAudioConfigRef.current);
  };

  const defaultTtsProfile = (product: 'dora' | 'soulmate'): TTSProductProfile => ({
    product,
    enabled: true,
    provider: audioConfig?.tts.provider || "zhipu",
    voice: product === "dora" ? "tongtong" : null,
    voice_strategy: product === "soulmate" ? "profile_match" : "fixed",
    speed: audioConfig?.tts.default_speed ?? 1.0,
    streaming_enabled: audioConfig?.tts.streaming_enabled ?? true,
    preamble_trim_ms: audioConfig?.tts.preamble_trim_ms ?? 500,
    provider_extra: {},
  });

  const defaultVoiceCallProfile = (product: 'dora' | 'soulmate'): VoiceCallProductProfile => ({
    product,
    enabled: audioConfig?.voice_call.enabled ?? false,
    provider: audioConfig?.voice_call.provider || "zhipu",
    model: audioConfig?.voice_call.model || "glm-realtime-flash",
    voice: product === "dora" ? "tongtong" : null,
    voice_strategy: product === "soulmate" ? "profile_match" : "fixed",
    interrupt_enabled: audioConfig?.voice_call.interrupt_enabled ?? true,
    vad_mode: audioConfig?.voice_call.vad_mode || "server_vad",
    transport: "websocket",
  });

  const getTtsProfile = (product: 'dora' | 'soulmate') =>
    audioConfig?.tts.profiles?.[product] || defaultTtsProfile(product);

  const getVoiceCallProfile = (product: 'dora' | 'soulmate') =>
    audioConfig?.voice_call.profiles?.[product] || defaultVoiceCallProfile(product);

  const updateTtsProfile = (
    product: 'dora' | 'soulmate',
    field: keyof TTSProductProfile,
    value: unknown,
  ) => {
    if (!audioConfig) return;
    const nextValue = product === "dora" && field === "voice_strategy" ? "fixed" : value;
    const updated = {
      ...audioConfig,
      tts: {
        ...audioConfig.tts,
        profiles: {
          ...audioConfig.tts.profiles,
          [product]: {
            ...getTtsProfile(product),
            [field]: nextValue,
          },
        },
      },
    };
    setAudioConfig(updated);
    onAudioDirtyChange?.(JSON.stringify(updated) !== initialAudioConfigRef.current);
  };

  const updateVoiceCallProfile = (
    product: 'dora' | 'soulmate',
    field: keyof VoiceCallProductProfile,
    value: unknown,
  ) => {
    if (!audioConfig) return;
    const updated = {
      ...audioConfig,
      voice_call: {
        ...audioConfig.voice_call,
        profiles: {
          ...audioConfig.voice_call.profiles,
          [product]: {
            ...getVoiceCallProfile(product),
            [field]: value,
          },
        },
      },
    };
    setAudioConfig(updated);
    onAudioDirtyChange?.(JSON.stringify(updated) !== initialAudioConfigRef.current);
  };

  const handleSaveAudio = async () => {
    if (!audioConfig) return;
    const normalized = {
      ...audioConfig,
      tts: {
        ...audioConfig.tts,
        profiles: {
          ...audioConfig.tts.profiles,
          dora: {
            ...getTtsProfile("dora"),
            voice_strategy: "fixed" as const,
          },
        },
      },
    };
    await audioApi.updateConfig(normalized);
  };

  React.useImperativeHandle(ref, () => ({
    saveAudio: handleSaveAudio,
    hasAudioChanges: () => JSON.stringify(audioConfig) !== initialAudioConfigRef.current,
  }));

  const handleProviderChange = (provider: string) => {
    updateAudioField("tts", "provider", provider);
    fetchProviderMeta(provider);
  };

  const handleVoiceCallProviderChange = (provider: string) => {
    updateAudioField("voice_call", "provider", provider);
    fetchVoiceCallMeta(provider);
  };

  const renderTtsProductProfile = (product: 'dora' | 'soulmate') => {
    const profile = getTtsProfile(product);
    const voiceStrategy = product === "dora" ? "fixed" : profile.voice_strategy;
    const title = product === "dora" ? "Dora TTS" : "Soulmate TTS";
    return (
      <div key={`tts-${product}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="text-sm font-semibold text-gray-800">{title}</h5>
            <p className="text-[11px] text-gray-500">
              {product === "dora" ? "Dora 固定音色/语速预览" : "按 Soulmate 人格 profile 匹配音色"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => handlePreview(product)}
            className={`px-2 py-1.5 rounded border text-sm flex items-center gap-1 transition-colors ${
              isPreviewing
                ? "bg-red-50 border-red-300 text-red-600 hover:bg-red-100"
                : "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
            }`}
            title={isPreviewing ? "停止" : "试听"}
          >
            {isPreviewing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            <span>{isPreviewing ? "停止" : "试听"}</span>
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">启用</label>
            <input
              type="checkbox"
              checked={profile.enabled}
              onChange={(e) => updateTtsProfile(product, "enabled", e.target.checked)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.ttsProvider}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={profile.provider}
              onChange={(e) => updateTtsProfile(product, "provider", e.target.value)}
            >
              <option value="zhipu">zhipu</option>
              <option value="minimax">minimax</option>
            </select>
          </div>
          <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">{at.voiceCallVoiceStrategy}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={voiceStrategy}
              onChange={(e) => updateTtsProfile(product, "voice_strategy", e.target.value)}
            >
              <option value="fixed">{at.voiceCallVoiceFixed}</option>
              {product === "soulmate" && <option value="profile_match">{at.voiceCallVoiceSoulmate}</option>}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.ttsVoice}</label>
            <input
              className="w-full px-2 py-1.5 text-sm border rounded"
              value={profile.voice || ""}
              onChange={(e) => updateTtsProfile(product, "voice", e.target.value || null)}
              disabled={voiceStrategy === "profile_match"}
              placeholder="tongtong / chuichui / xiaochen / voice_clone_xxx"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              {at.ttsSpeed}: {(profile.speed ?? audioConfig?.tts.default_speed ?? 1.0).toFixed(1)}
            </label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              className="w-full"
              value={profile.speed ?? audioConfig?.tts.default_speed ?? 1.0}
              onChange={(e) => updateTtsProfile(product, "speed", parseFloat(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.ttsPreambleTrim}</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max="5000"
                step="50"
                className="w-24 px-2 py-1 text-sm border rounded"
                value={profile.preamble_trim_ms ?? audioConfig?.tts.preamble_trim_ms ?? 500}
                onChange={(e) => updateTtsProfile(product, "preamble_trim_ms", parseInt(e.target.value) || 0)}
              />
              <span className="text-xs text-gray-500">{at.ttsPreambleTrimUnit}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVoiceCallProductProfile = (product: 'dora' | 'soulmate') => {
    const profile = getVoiceCallProfile(product);
    const title = product === "dora" ? "Dora 实时语音" : "Soulmate 实时语音";
    return (
      <div key={`voice-${product}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-3">
        <div>
          <h5 className="text-sm font-semibold text-gray-800">{title}</h5>
          <p className="text-[11px] text-gray-500">
            {product === "dora" ? "Dora 主提示词 + 固定音色" : "Soulmate personality/profile 注入 + 人格匹配音色"}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallEnabled}</label>
            <input
              type="checkbox"
              checked={profile.enabled}
              onChange={(e) => updateVoiceCallProfile(product, "enabled", e.target.checked)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallProvider}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={profile.provider}
              onChange={(e) => updateVoiceCallProfile(product, "provider", e.target.value)}
            >
              <option value="zhipu">zhipu</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallModel}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={profile.model}
              onChange={(e) => updateVoiceCallProfile(product, "model", e.target.value)}
            >
              {(voiceCallMeta?.models || []).map((model) => (
                <option key={model.id} value={model.id}>{model.name}</option>
              ))}
              {(!voiceCallMeta?.models || voiceCallMeta.models.length === 0) && (
                <option value={profile.model}>{profile.model}</option>
              )}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallVoiceStrategy}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={profile.voice_strategy}
              onChange={(e) => updateVoiceCallProfile(product, "voice_strategy", e.target.value)}
            >
              <option value="fixed">{at.voiceCallVoiceFixed}</option>
              <option value="profile_match">{at.voiceCallVoiceSoulmate}</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallFixedVoice}</label>
            <input
              className="w-full px-2 py-1.5 text-sm border rounded"
              value={profile.voice || ""}
              onChange={(e) => updateVoiceCallProfile(product, "voice", e.target.value || null)}
              disabled={profile.voice_strategy === "profile_match"}
              placeholder="tongtong / chuichui / xiaochen / voice_clone_xxx"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallVadMode}</label>
            <select
              className="w-full px-2 py-1.5 text-sm border rounded bg-white"
              value={profile.vad_mode}
              onChange={(e) => updateVoiceCallProfile(product, "vad_mode", e.target.value)}
            >
              <option value="server_vad">server_vad</option>
              <option value="client_vad">client_vad</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-600">{at.voiceCallInterrupt}</label>
            <input
              type="checkbox"
              checked={profile.interrupt_enabled}
              onChange={(e) => updateVoiceCallProfile(product, "interrupt_enabled", e.target.checked)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <ModelCategoryCard
      title={t.speechModels}
      icon={<Mic className="w-5 h-5 text-pink-600" />}
    >
      {/* Existing model selectors */}
      <ModelField
        label={t.sttModel}
        value={formData.stt.model_id}
        onChange={(v) => updateField("stt", "model_id", v)}
        description={t.sttModelDesc}
        modality="stt"
      />
      <ModelField
        label={t.ttsModel}
        value={formData.tts.model_id}
        onChange={(v) => updateField("tts", "model_id", v)}
        description={t.ttsModelDesc}
        modality="tts"
        directConnect
      />

      {/* Audio Settings */}
      {!loadingAudio && audioConfig && (
        <div className="sm:col-span-2 mt-2 pt-3 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">{at.title}</h4>

          {/* TTS Settings */}
          <div className="grid gap-3 sm:grid-cols-2 mb-4">
            {/* TTS Provider */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">{at.ttsProvider}</label>
              <select
                className="w-full px-2 py-1.5 text-sm border rounded bg-white"
                value={audioConfig.tts.provider}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                <option value="zhipu">zhipu</option>
                <option value="minimax">minimax</option>
              </select>
            </div>

            {/* TTS Voice */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">{at.ttsVoice}</label>
              <div className="flex gap-1.5">
              <select
                className="flex-1 px-2 py-1.5 text-sm border rounded bg-white"
                value={
                  (audioConfig.tts.providers?.[audioConfig.tts.provider] as Record<string, unknown>)?.voice as string || ""
                }
                onChange={(e) => {
                  const provider = audioConfig.tts.provider;
                  const updated = {
                    ...audioConfig,
                    tts: {
                      ...audioConfig.tts,
                      providers: {
                        ...audioConfig.tts.providers,
                        [provider]: {
                          ...(audioConfig.tts.providers?.[provider] || {}),
                          voice: e.target.value,
                        },
                      },
                    },
                  };
                  setAudioConfig(updated);
                  onAudioDirtyChange?.(JSON.stringify(updated) !== initialAudioConfigRef.current);
                }}
              >
                {providerMeta?.voices?.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
                {(!providerMeta?.voices || providerMeta.voices.length === 0) && (
                  <option value="">--</option>
                )}
              </select>
              <button
                type="button"
                onClick={() => handlePreview()}
                className={`px-2 py-1.5 rounded border text-sm flex items-center gap-1 transition-colors ${
                  isPreviewing
                    ? "bg-red-50 border-red-300 text-red-600 hover:bg-red-100"
                    : "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                }`}
                title={isPreviewing ? "停止" : "试听"}
              >
                {isPreviewing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
              </button>
              </div>
            </div>

            {/* TTS Speed */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">
                {at.ttsSpeed}: {audioConfig.tts.default_speed?.toFixed(1) ?? "1.0"}
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                className="w-full"
                value={audioConfig.tts.default_speed ?? 1.0}
                onChange={(e) =>
                  updateAudioField("tts", "default_speed", parseFloat(e.target.value))
                }
              />
              <div className="flex justify-between text-[10px] text-gray-400">
                <span>0.5</span>
                <span>1.0</span>
                <span>2.0</span>
              </div>
            </div>

            {/* Preamble Trim */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">{at.ttsPreambleTrim}</label>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="5000"
                  step="50"
                  className="w-24 px-2 py-1 text-sm border rounded"
                  value={audioConfig.tts.preamble_trim_ms ?? 500}
                  onChange={(e) =>
                    updateAudioField("tts", "preamble_trim_ms", parseInt(e.target.value) || 0)
                  }
                />
                <span className="text-xs text-gray-500">{at.ttsPreambleTrimUnit}</span>
              </div>
            </div>

            {/* Streaming Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600">{at.ttsStreaming}</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={audioConfig.tts.streaming_enabled ?? false}
                  onChange={(e) =>
                    updateAudioField("tts", "streaming_enabled", e.target.checked)
                  }
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
            </div>

            {/* STT Hotwords Toggle */}
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-600">{at.sttHotwords}</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={audioConfig.stt.hotwords_enabled ?? false}
                  onChange={(e) =>
                    updateAudioField("stt", "hotwords_enabled", e.target.checked)
                  }
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
              </label>
            </div>
          </div>

          <div className="sm:col-span-2 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-pink-600" />
              <h4 className="text-sm font-semibold text-gray-700">产品 TTS 配置</h4>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {renderTtsProductProfile("dora")}
              {renderTtsProductProfile("soulmate")}
            </div>
          </div>

          {/* Voice Call Settings */}
          <div className="sm:col-span-2 mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <PhoneCall className="w-4 h-4 text-pink-600" />
              <h4 className="text-sm font-semibold text-gray-700">{at.voiceCallTitle}</h4>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                直连 · Direct
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mb-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">全局开关</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={audioConfig.voice_call.enabled ?? false}
                    onChange={(e) =>
                      updateAudioField("voice_call", "enabled", e.target.checked)
                    }
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">默认服务商</label>
                <select
                  className="w-full px-2 py-1.5 text-sm border rounded bg-white"
                  value={audioConfig.voice_call.provider}
                  onChange={(e) => handleVoiceCallProviderChange(e.target.value)}
                >
                  <option value="zhipu">zhipu</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">默认模型</label>
                <select
                  className="w-full px-2 py-1.5 text-sm border rounded bg-white"
                  value={audioConfig.voice_call.model}
                  onChange={(e) =>
                    updateAudioField("voice_call", "model", e.target.value)
                  }
                >
                  {(voiceCallMeta?.models || []).map((model) => (
                    <option key={model.id} value={model.id}>{model.name}</option>
                  ))}
                  {(!voiceCallMeta?.models || voiceCallMeta.models.length === 0) && (
                    <option value={audioConfig.voice_call.model}>{audioConfig.voice_call.model}</option>
                  )}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">{at.voiceCallFallbackModel}</label>
                <select
                  className="w-full px-2 py-1.5 text-sm border rounded bg-white"
                  value={audioConfig.voice_call.fallback_model || ""}
                  onChange={(e) =>
                    updateAudioField("voice_call", "fallback_model", e.target.value || null)
                  }
                >
                  <option value="">{at.voiceCallNoFallback}</option>
                  {(voiceCallMeta?.models || []).map((model) => (
                    <option key={model.id} value={model.id}>{model.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {renderVoiceCallProductProfile("dora")}
              {renderVoiceCallProductProfile("soulmate")}
            </div>
          </div>

        </div>
      )}
    </ModelCategoryCard>
  );
});

export const ModelDefaultsPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.modelDefaults!;

  const { data: config, isLoading, error, refetch } = useModelDefaultsConfig();
  const updateConfig = useUpdateModelDefaultsConfig();

  const [formData, setFormData] = useState<ModelDefaultsConfig | null>(null);
  const [audioDirty, setAudioDirty] = useState(false);
  const speechSectionRef = useRef<SpeechModelsSectionHandle>(null);

  useEffect(() => {
    if (config) {
      setFormData({
        ...config,
        image: config.image || { model_id: "" },
        prompt_rewrite: config.prompt_rewrite || { model_id: "" },
        mbti: config.mbti || { model_id: "" },
      });
    }
  }, [config]);

  const handleSave = async () => {
    if (!formData) return;

    // Remove metadata before saving
    const { version: _version, updated_by: _updated_by, updated_at: _updated_at, ...configToSave } = formData;

    try {
      await updateConfig.mutateAsync(configToSave as ModelDefaultsConfig);
      await speechSectionRef.current?.saveAudio();
      setAudioDirty(false);
      alert(t.saveSuccess);
    } catch (err) {
      alert(`${t.saveFailed}: ${err}`);
    }
  };

  const handleReload = () => {
    refetch();
  };

  const updateField = (
    category: ModelDefaultsCategory,
    field: string,
    value: any,
  ) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [category]: {
        ...formData[category],
        [field]: value,
      },
    });
  };

  if (isLoading) {
    return <Loading message={t.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>
          {t.loadFailed}: {error.message}
        </span>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  const hasModelChanges = JSON.stringify(formData) !== JSON.stringify(config);
  const hasChanges = hasModelChanges || audioDirty;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{t.title}</h2>
          <p className="text-sm text-gray-500 mt-1">{t.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={handleReload}
            disabled={updateConfig.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {t.reload}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-2" />
            {updateConfig.isPending ? t.saving : t.save}
          </Button>
        </div>
      </div>

      {/* Section 1: Global Models */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t.globalModelsTitle}
        </h2>
        <p className="text-sm text-gray-500 mb-4">{t.globalModelsDesc}</p>
      </div>

      {/* Memory Model */}
      <ModelCategoryCard
        title={t.memoryModel}
        icon={<Brain className="w-5 h-5 text-green-600" />}
        badge={t.allAgentsShared}
      >
        <ModelField
          label={t.memoryModel}
          value={formData.memory.model_id}
          onChange={(v) => updateField("memory", "model_id", v)}
          description={t.memoryModelDesc}
          modality="text"
        />
        <div />
        <div className="sm:col-span-2">
          <ModelParams
            category="memory"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* MBTI Inference Model */}
      <ModelCategoryCard
        title={t.mbtiModel}
        icon={<Sparkles className="w-5 h-5 text-indigo-600" />}
        badge={t.allAgentsShared}
      >
        <div>
          <ModelField
            label={t.mbtiModel}
            value={formData.mbti?.model_id || ""}
            onChange={(v) => updateField("mbti" as ModelDefaultsCategory, "model_id", v)}
            description={t.mbtiModelDesc}
            modality="text"
          />
          <ModelParams
            category={"mbti" as ModelDefaultsCategory}
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* Embedding & Reranking Models */}
      <ModelCategoryCard
        title={t.embeddingReranking}
        icon={<ArrowUpDown className="w-5 h-5 text-orange-600" />}
        badge={t.allAgentsShared}
      >
        <ModelField
          label={t.embeddingModel}
          value={formData.embedding.model_id}
          onChange={(v) => updateField("embedding", "model_id", v)}
          description={t.embeddingModelDesc}
          modality="embedding"
        />
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">{t.rerankingEnabled}</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={formData.reranking.enabled !== false}
                onChange={(e) => updateField("reranking", "enabled", e.target.checked)}
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className={formData.reranking.enabled === false ? "opacity-40 pointer-events-none" : ""}>
            <ModelField
              label=""
              value={formData.reranking.model_id}
              onChange={(v) => updateField("reranking", "model_id", v)}
              description={t.rerankingModelDesc}
              modality="rerank"
            />
            <ModelParams
              category="reranking"
              formData={formData}
              updateField={updateField}
              hasTopK
            />
          </div>
        </div>
      </ModelCategoryCard>

      {/* Section 2: Per-Agent Defaults */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t.perAgentDefaultsTitle}
        </h2>
        <p className="text-sm text-gray-500 mb-4">{t.perAgentDefaultsDesc}</p>
      </div>

      {/* Chat Models */}
      <ModelCategoryCard
        title={t.chatModels}
        icon={<MessageSquare className="w-5 h-5 text-blue-600" />}
      >
        <ModelField
          label={t.primaryChatModel}
          value={formData.chat.model_id}
          onChange={(v) => updateField("chat", "model_id", v)}
          description={t.primaryChatModelDesc}
          modality="text"
          capabilityFilter="function_calling"
        />
        <ModelField
          label={t.quickChatModel}
          value={formData.chat.quick_model_id}
          onChange={(v) => updateField("chat", "quick_model_id", v)}
          description={t.quickChatModelDesc}
          modality="text"
          capabilityFilter="function_calling"
        />
        <div className="sm:col-span-2">
          <ModelParams
            category="chat"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
      </ModelCategoryCard>

      {/* Vision Models */}
      <ModelCategoryCard
        title={t.visionModels}
        icon={<Eye className="w-5 h-5 text-purple-600" />}
      >
        <ModelField
          label={t.primaryVisionModel}
          value={formData.vision.model_id}
          onChange={(v) => updateField("vision", "model_id", v)}
          description={t.primaryVisionModelDesc}
          modality="text"
          capabilityFilter="vision"
        />
        <ModelField
          label={t.quickVisionModel}
          value={formData.vision.quick_model_id}
          onChange={(v) => updateField("vision", "quick_model_id", v)}
          description={t.quickVisionModelDesc}
          modality="text"
          capabilityFilter="vision"
        />
        <div className="sm:col-span-2">
          <ModelParams
            category="vision"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
      </ModelCategoryCard>

      {/* Auxiliary Models */}
      <ModelCategoryCard
        title={t.auxiliaryModels}
        icon={<Brain className="w-5 h-5 text-green-600" />}
      >
        <div>
          <ModelField
            label={t.classificationModel}
            value={formData.classification.model_id}
            onChange={(v) => updateField("classification", "model_id", v)}
            description={t.classificationModelDesc}
            modality="text"
          />
          <ModelParams
            category="classification"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
            hasMaxTokens
          />
        </div>
        <div>
          <ModelField
            label={t.reasoningModel}
            value={formData.reasoning.model_id}
            onChange={(v) => updateField("reasoning", "model_id", v)}
            description={t.reasoningModelDesc}
            modality="text"
            capabilityFilter="reasoning"
          />
          <ModelParams
            category="reasoning"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasTopP
          />
        </div>
        <div>
          <ModelField
            label={t.taskModel}
            value={formData.task?.model_id || ""}
            onChange={(v) => updateField("task", "model_id", v)}
            description={t.taskModelDesc}
            modality="text"
          />
          <ModelParams
            category="task"
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* Prompt Rewrite Model */}
      <ModelCategoryCard
        title={t.promptRewriteModel}
        icon={<Sparkles className="w-5 h-5 text-violet-600" />}
        badge={t.allAgentsShared}
      >
        <div>
          <ModelField
            label={t.promptRewriteModel}
            value={formData.prompt_rewrite?.model_id || ""}
            onChange={(v) => updateField("prompt_rewrite" as ModelDefaultsCategory, "model_id", v)}
            description={t.promptRewriteModelDesc}
            modality="text"
          />
          <ModelParams
            category={"prompt_rewrite" as ModelDefaultsCategory}
            formData={formData}
            updateField={updateField}
            hasTemperature
            hasMaxTokens
          />
        </div>
      </ModelCategoryCard>

      {/* Speech Models */}
      <SpeechModelsSection
        ref={speechSectionRef}
        formData={formData}
        updateField={updateField}
        t={t}
        onAudioDirtyChange={setAudioDirty}
      />

      {/* Image Generation Model */}
      <ModelCategoryCard
        title={t.imageGenerationModels}
        icon={<ImageIcon className="w-5 h-5 text-cyan-600" />}
        badge={t.allAgentsShared}
      >
        <div>
          <p className="text-xs text-muted-foreground mb-3">{t.imageModelDesc}</p>
          <ModelSelect
            modality="image"
            value={formData.image?.model_id || ""}
            onChange={(v) =>
              updateField("image" as ModelDefaultsCategory, "model_id", v)
            }
            variant="compact"
            operationFilter="generate"
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-3">{t.qualityImageModelDesc}</p>
          <ModelSelect
            modality="image"
            value={formData.image?.quality_model_id || ""}
            onChange={(v) =>
              updateField("image" as ModelDefaultsCategory, "quality_model_id", v)
            }
            variant="compact"
            operationFilter="generate"
          />
        </div>
      </ModelCategoryCard>

      {/* Metadata Footer */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500 border-t border-gray-100 pt-4">
          <p>
            {t.version}: <span className="font-medium">{config.version}</span> |
            {t.lastUpdatedBy}:{" "}
            <span className="font-medium">{config.updated_by || "N/A"}</span> |
            <span className="ml-1">
              {config.updated_at
                ? new Date(config.updated_at).toLocaleString()
                : "N/A"}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
